import { useRef } from 'react';

import { mergeRight } from 'ramda';

import { IPage } from 'utils/itineraryDetails/types';

import { ILandingConfig } from '../types';
import { PAGES_CONFIG_BY_ROLE, PAGES_CONFIG_BY_TYPE } from './config';
import { IPageContext } from './context';
import { EPageLayout, EPageOrder, IPageConfig, IPageProps } from './types';

export function usePageContextValue(props: IPageProps): IPageContext {
  const { data, readonly, landingConfig, onChangeCoverImage, onChange, index, isLast, isFirst, virtualized } = props;
  const leftSideRef = useRef(null);
  const rightSideRef = useRef(null);
  const pageHeadlineRef = useRef(null);

  const config: IPageConfig = getPageConfig({ data, landingConfig, isFirst, isLast });

  return {
    data,
    index,
    leftSideRef,
    rightSideRef,
    pageHeadlineRef,
    readonly,
    config,
    onChange,
    onChangeCoverImage,
    ...(virtualized && {
      rightSideRef: virtualized.measureElement,
      virtualized: {
        rendered: virtualized.rendered,
        height: virtualized.height,
      },
    }),
  };
}

type TGetPageConfigArgs = {
  data: IPage;
  landingConfig: ILandingConfig;
  isFirst: boolean;
  isLast: boolean;
};

function getPageConfig({ data: page, landingConfig, isFirst, isLast }: TGetPageConfigArgs): IPageConfig {
  const innerConfig: IPageConfig = mergeRight(PAGES_CONFIG_BY_TYPE[page.type], PAGES_CONFIG_BY_ROLE[page.role]);
  const outerConfig: IPageConfig = landingConfig.pages.reduce(
    (result, cur) => {
      const isTypeSucceeded = Array.isArray(cur.types) ? cur.types.includes(page.type) : true;
      const isRoleSucceeded = Array.isArray(cur.roles) ? cur.roles.includes(page.role) : true;
      const isOrderSucceeded = Array.isArray(cur.order)
        ? (cur.order.includes(EPageOrder.FIRST) && isFirst) || (cur.order.includes(EPageOrder.LAST) && isLast)
        : true;
      if (isTypeSucceeded && isRoleSucceeded && isOrderSucceeded) return { layout: cur.layout };

      return result;
    },
    { layout: EPageLayout.HalfWidth },
  );

  return { ...mergeRight(innerConfig, outerConfig), settings: landingConfig.settings };
}
